"use client";

import React, { useEffect } from "react";
import { Phone, Clock, ShieldCheck, Building2, FileCheck, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
export default function RequestService() {
  useEffect(() => {
    const cssId = "jobber-embed-css";
    if (!document.getElementById(cssId)) {
      const link = document.createElement("link");
      link.id = cssId;
      link.rel = "stylesheet";
      link.media = "screen";
      link.href = "https://d3ey4dbjkt2f6s.cloudfront.net/assets/external/work_request_embed.css";
      document.head.appendChild(link);
    }
    const script = document.createElement("script");
    script.src = "https://d3ey4dbjkt2f6s.cloudfront.net/assets/static_link/work_request_embed_snippet.js";
    script.setAttribute("clienthub_id", "476c5fe2-f16a-4108-ad33-1763e02c3dad-4972587");
    script.setAttribute("form_url", "https://clienthub.getjobber.com/client_hubs/476c5fe2-f16a-4108-ad33-1763e02c3dad/public/work_request/embedded_work_request_form?form_id=4972587");
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);
  return <section id="request-service" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-t border-border overflow-hidden">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Technical eyebrow & header */}
        <div className="max-w-3xl mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-primary/10 border border-primary/25 text-primary text-xs font-mono font-bold tracking-wider uppercase mb-4">
            <span className="size-2 rounded-full bg-primary animate-pulse" />
            Direct Commercial Dispatch • MD / DC / NOVA
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
            Request commercial emergency dispatch
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Directly connect your facility, HOA community, or commercial asset
            with TriServe supervisors. No off-site call centers, no delay
            protocols.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Main Form Column */}
          <div className="lg:col-span-7">
            <Card className="border border-border shadow-md bg-card rounded-lg overflow-hidden">
              <CardHeader className="border-b border-border bg-muted/30 pb-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-xl font-bold text-card-foreground">
                      Facility Intake & Urgent Response
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground mt-1">
                      Complete this intake for immediate mobilization and on-site mitigation.
                    </CardDescription>
                  </div>
                  <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded bg-accent/20 border border-accent text-accent-foreground text-xs font-mono font-semibold">
                    <Clock className="size-3.5" />
                    {"< 60 Min"}
                  </div>
                </div>
              </CardHeader>

              <CardContent className="pt-6">
                <div id="476c5fe2-f16a-4108-ad33-1763e02c3dad-4972587" />
              </CardContent>
            </Card>
          </div>

          {/* Direct Commercial Contact & PM Dispatch Card */}
          <div className="lg:col-span-5 space-y-6">
            {/* Urgent Dispatch Container */}
            <Card className="border border-border bg-secondary text-secondary-foreground shadow-lg rounded-lg overflow-hidden">
              <CardHeader className="pb-4">
                <div className="inline-flex items-center gap-2 text-accent font-mono text-xs font-bold uppercase tracking-wider">
                  <Phone className="size-3.5 animate-pulse" />
                  Direct Property Manager Line
                </div>
                <CardTitle className="text-2xl font-bold text-secondary-foreground mt-1">
                  Immediate 24/7 Command
                </CardTitle>
                <CardDescription className="text-secondary-foreground/80 text-sm">
                  Active disaster in multi-unit or commercial premises? Skip form
                  entry and engage directly with on-call restoration leads.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 rounded-md bg-secondary-foreground/10 border border-secondary-foreground/20 space-y-3">
                  <span className="text-xs font-mono uppercase tracking-wider text-secondary-foreground/70 block">
                    Direct Operations Hotlink
                  </span>
                  <a href="tel:855-435-6631" className="text-2xl sm:text-3xl font-mono font-extrabold text-accent hover:underline flex items-center gap-3 tracking-tight">
                    <Phone className="size-6 shrink-0" />
                    855-435-6631
                  </a>
                  <p className="text-xs text-secondary-foreground/80 leading-relaxed">
                    Operated directly by TriServe regional crews. No outsourced
                    call screening or third-party answering services.
                  </p>
                </div>

                <div className="space-y-3 border-t border-secondary-foreground/15 pt-5">
                  <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-accent">
                    Dedicated Protocol for HOAs & Facilities
                  </h3>
                  <ul className="space-y-2.5 text-xs text-secondary-foreground/90 leading-relaxed">
                    <li className="flex items-start gap-2.5">
                      <ShieldCheck className="size-4 text-accent shrink-0 mt-0.5" />
                      <span>
                        <strong>Multi-tenant containment:</strong> Rapid moisture
                        mapping to protect unaffected residential suites below.
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <FileCheck className="size-4 text-accent shrink-0 mt-0.5" />
                      <span>
                        <strong>Direct insurance file setup:</strong> Direct line
                        with commercial carriers and property adjusters from hour one.
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <Building2 className="size-4 text-accent shrink-0 mt-0.5" />
                      <span>
                        <strong>Board & tenant updates:</strong> Concise technical
                        documentation ready for association management reviews.
                      </span>
                    </li>
                  </ul>
                </div>

                <Button asChild className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold h-11">
                  <a href="tel:855-435-6631">
                    <Phone className="size-4" />
                    Call Regional Dispatch Now
                  </a>
                </Button>
              </CardContent>
            </Card>

            {/* Regional Coverage Spec Bar */}
            <div className="p-4 rounded-lg bg-card border border-border text-foreground space-y-3 shadow-xs">
              <div className="flex items-center gap-2 text-primary font-mono text-xs font-bold uppercase">
                <MapPin className="size-4" />
                Regional Coverage Bases
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Staged trucks and industrial equipment deployed across Montgomery
                County, Prince George&apos;s, Fairfax, Loudoun, and Washington DC.
              </p>
              <div className="grid grid-cols-3 gap-2 pt-2 border-t border-border text-center font-mono text-[11px]">
                <div className="p-2 rounded bg-muted">
                  <span className="font-bold block text-foreground">MD</span>
                  <span className="text-muted-foreground text-[10px]">Crews On-Call</span>
                </div>
                <div className="p-2 rounded bg-muted">
                  <span className="font-bold block text-foreground">DC</span>
                  <span className="text-muted-foreground text-[10px]">Metro Dispatch</span>
                </div>
                <div className="p-2 rounded bg-muted">
                  <span className="font-bold block text-foreground">VA</span>
                  <span className="text-muted-foreground text-[10px]">NoVA Response</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}